import { useState, useEffect } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { humanizeSnakeCase } from 'utils/format'
import { snakeCase } from 'change-object-case'
import {
  STATUSES,
  INITIAL_SORT_STATE,
  SORT_COLUMN_MAPS,
  COLUMN_DOWNLOAD_MAPS,
  UPLOAD_REQUEST_TABLE_NAME,
} from '../utils/constants'
import { getInitialSortState } from 'components/Table/Sorting/utils'
import { useFileDownload } from 'hooks/useFileDownload'
import { getUserUploads, getUserUploadsExportURL } from 'services/userUploadRequests'
import { NO_ASSIGNEE_OPTION } from '../utils/constants'

const useRequestFormFilters = () => {
  const { field, sort } = getInitialSortState(
    UPLOAD_REQUEST_TABLE_NAME,
    INITIAL_SORT_STATE,
    ({ sort, field }) => ({
      sort: sort || INITIAL_SORT_STATE.sort,
      field: SORT_COLUMN_MAPS[field] || INITIAL_SORT_STATE.field,
    }),
  )

  const STATUS_OPTIONS = STATUSES.map(status => ({
    label: humanizeSnakeCase(status),
    value: status,
  }))

  const DEFAULT_QUERY_PARAMS = {
    pageOffset: 0,
    page: 0,
    pageSize: 50,
    customers: [],
    assignee: {},
    customerId: null,
    assigneeId: null,
    name: null,
    status: STATUSES.filter(status => status !== 'completed'),
    overdue: false,
    priority: [],
    sort: `${field}_${sort}`,
  }

  const { download, isLoading: isDownloading } = useFileDownload()
  const [queryParams, setQueryParams] = useState(DEFAULT_QUERY_PARAMS)

  const params = {
    pageOffset: queryParams.pageOffset,
    pageSize: queryParams.pageSize,
    customerId: queryParams.customerId,
    assigneeId: queryParams.assigneeId,
    status: queryParams.status,
    priority: queryParams.priority,
    sort: queryParams.sort,
    term: queryParams.name,
    overdue: queryParams.overdue,
  }

  const {
    data: { data: uploadRequests, meta },
    isFetching,
  } = useQuery({
    queryKey: ['useUploadRequestsQuery', params],
    queryFn: () => getUserUploads(params),
    initialData: { data: [], meta: { itemCount: 0, totalOverdueCount: 0, assignees: [] } },
    refetchOnWindowFocus: false,
    throwOnError: true,
    placeholderData: keepPreviousData,
  })

  const handleCSVDownload = getExportableColumns => {
    const columns = getExportableColumns(UPLOAD_REQUEST_TABLE_NAME)
      // TODO: CreatedAt will be requestedAt, and so we can remove the filter here for createdAt.
      .filter(col => col !== 'createdAt')
      .map(col => COLUMN_DOWNLOAD_MAPS[col] || snakeCase(col))

    const url = getUserUploadsExportURL({ ...params, columns })
    download(url)
  }

  const handleOnSort = (column, direction) => {
    const field = SORT_COLUMN_MAPS[column]

    setQueryParams({ ...queryParams, sort: `${field}_${direction}` })
  }

  const handlePageChange = page => {
    setQueryParams({ ...queryParams, page, pageOffset: page * queryParams.pageSize })
  }

  const handlePageSizeChange = pageSize => {
    setQueryParams({ ...queryParams, pageSize, page: 0, pageOffset: 0 })
  }

  const handleCustomerChange = customers => {
    const customerId = customers ? customers.map(customer => customer.id) : []
    setQueryParams({
      ...queryParams,
      customerId,
      customers: customers || [],
      pageOffset: 0,
    })
  }

  const handleStatusChange = params => {
    setQueryParams({ ...queryParams, status: params.map(p => p.value), pageOffset: 0 })
  }

  const handleNameChange = name => {
    setQueryParams({ ...queryParams, name, pageOffset: 0 })
  }

  const handleAssigneeChange = assignee => {
    // Handle "No assignee" option by passing special value to API
    const assigneeId = assignee.value === NO_ASSIGNEE_OPTION.value ? 'none' : assignee.value
    setQueryParams({ ...queryParams, assigneeId, assignee, pageOffset: 0 })
  }

  const handleOverdueChange = overdue => {
    setQueryParams({ ...queryParams, overdue, pageOffset: 0 })
  }

  const handlePriorityChange = priorities => {
    setQueryParams({ ...queryParams, priority: priorities.map(p => p.value), pageOffset: 0 })
  }

  useEffect(() => {
    if (meta.totalOverdueCount === 0) {
      setQueryParams(prev => ({ ...prev, overdue: false }))
    }
  }, [meta.totalOverdueCount])

  return {
    STATUS_OPTIONS,
    queryParams,
    uploadRequests,
    itemCount: meta.itemCount,
    overdueCount: meta.totalOverdueCount,
    assignees: meta.assignees,
    isFetching,
    isDownloading,
    page: queryParams.page,
    params,
    handleCSVDownload,
    handleOnSort,
    handlePageChange,
    handlePageSizeChange,
    handleCustomerChange,
    handleStatusChange,
    handleNameChange,
    handleAssigneeChange,
    handleOverdueChange,
    handlePriorityChange,
  }
}

export default useRequestFormFilters
